import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SECTION } from "@/constants/examConstants";
import { delay } from "@/utils/common";

const READING_TIME_SECONDS = 75 * 60;

/**
 * Single source of truth for the TOEIC exam flow state machine, shared
 * between the authenticated `ExamSession` and the public `PublicMockTest`
 * runner. It owns:
 *
 *   - position state: currentPartIndex / currentGroupIndex / showInstructions
 *   - audio: ref, play/pause, auto-advance on `onEnded`, transition timer
 *   - navigation: handleNext / handlePrev / navigateToQuestion
 *   - reading timer: 75-minute countdown for Reading sections
 *   - welcome screen + sound test gating
 *
 * Test-specific concerns (saving answers to the server, submitting, result
 * dialogs) are deliberately NOT in this hook — the caller plugs them in via
 * `onTimeUp` and decides what to render at each phase.
 *
 * The two consumers differ only in:
 *   - `mode === "part"`     → skip welcome screen, no reading timer
 *   - `mode === "full"`     → full TOEIC flow with welcome + 75-min timer
 *
 * @param {object}   params
 * @param {object}   params.exam            Normalized exam (see normalizeExam)
 * @param {"full"|"part"} params.mode       Drives welcome screen + timer
 * @param {function} params.onTimeUp        Called when the reading timer hits 0
 */
const useExamFlow = ({ exam, mode = "full", onTimeUp }) => {
  // Memoize so downstream useMemo/useCallback deps stay stable when the
  // caller passes a fresh `exam` object on every render but the parts
  // array is structurally the same.
  const parts = useMemo(() => exam?.parts || [], [exam]);
  const partsLength = parts.length;

  // ── Phase / position ────────────────────────────────────────────────
  // In part mode the student opted into single-part practice — there is no
  // welcome screen, no whole-test directions, and no per-part instructions.
  // Going straight to questions also unblocks the navigation bar (which is
  // gated on `!showInstructions`) so the question palette stays reachable.
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(mode === "full");
  const [showInstructions, setShowInstructions] = useState(mode === "full");
  const [hasUserInteracted, setHasUserInteracted] = useState(mode !== "full");
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [seenPartInstructions, setSeenPartInstructions] = useState([]);

  // ── Audio ───────────────────────────────────────────────────────────
  const audioRef = useRef(null);
  const audioTransitionTimerRef = useRef(null);
  const [audioVolume, setAudioVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFinishListening, setIsFinishListening] = useState(false);

  // ── Reading timer ───────────────────────────────────────────────────
  const [timeLeft, setTimeLeft] = useState(READING_TIME_SECONDS);
  const [isWaiting, setIsWaiting] = useState(false);
  const timerEndTimeRef = useRef(null);
  // Latest onTimeUp without forcing the timer effect to restart whenever the
  // caller passes a new closure (which it always does when answers change).
  const onTimeUpRef = useRef(onTimeUp);
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  // ── Derived ─────────────────────────────────────────────────────────
  const currentPart = useMemo(
    () => parts[currentPartIndex] || null,
    [parts, currentPartIndex]
  );
  const questionGroup = useMemo(
    () => currentPart?.questionGroup || [],
    [currentPart]
  );
  const currentSection = useMemo(
    () => currentPart?.section || SECTION.LISTENING,
    [currentPart]
  );
  const currentGroup = useMemo(
    () => questionGroup[currentGroupIndex] || null,
    [questionGroup, currentGroupIndex]
  );

  const isLastQuestion = useMemo(() => {
    if (!currentPart) return false;
    return (
      currentPartIndex === partsLength - 1 &&
      currentGroupIndex === (currentPart.questionGroup?.length ?? 0) - 1
    );
  }, [currentPart, currentPartIndex, currentGroupIndex, partsLength]);

  // Generalised "last listening part" detection so part-mode subsets
  // (e.g. Part 3 only) work without the hard-coded `currentPartIndex === 3`
  // that the legacy ExamSession used.
  const lastListeningPartNumber = useMemo(() => {
    const listening = parts.filter((p) => p.section === SECTION.LISTENING);
    return listening.length ? listening[listening.length - 1].partNumber : null;
  }, [parts]);

  // Cleanup any pending audio transition on unmount.
  useEffect(() => {
    return () => {
      if (audioTransitionTimerRef.current) {
        clearTimeout(audioTransitionTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showWelcomeScreen) window.scrollTo(0, 0);
  }, [showWelcomeScreen]);

  useEffect(() => {
    if (isFinishListening) {
      window.scrollTo(0, document.body.scrollHeight - 1100);
    }
  }, [isFinishListening]);

  // ── Audio playback ──────────────────────────────────────────────────
  const playAudio = useCallback(
    async (src) => {
      try {
        if (currentSection === SECTION.READING) return false;
        if (!audioRef.current) {
          await delay(500);
          if (!audioRef.current) return false;
        }
        if (audioTransitionTimerRef.current) {
          clearTimeout(audioTransitionTimerRef.current);
          audioTransitionTimerRef.current = null;
        }
        audioRef.current.pause();
        if (audioVolume) audioRef.current.volume = audioVolume;
        if (!src) return false;
        audioRef.current.src = src;
        if (process.env.NODE_ENV === "development") {
          audioRef.current.playbackRate = 2;
        }
        await delay(100);
        await audioRef.current.play();
        return true;
      } catch (_) {
        return false;
      }
    },
    [audioVolume, currentSection]
  );

  const handleNextPart = useCallback(() => {
    window.scrollTo(0, 0);
    if (currentPartIndex < partsLength - 1) {
      const nextPartIndex = currentPartIndex + 1;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = "";
      }
      if (audioTransitionTimerRef.current) {
        clearTimeout(audioTransitionTimerRef.current);
        audioTransitionTimerRef.current = null;
      }
      // Part mode never shows per-part instructions; full mode always does
      // when entering a new part for the first time.
      setShowInstructions(mode === "full");
      setCurrentPartIndex(nextPartIndex);
      setCurrentGroupIndex(0);
    }
  }, [currentPartIndex, partsLength, mode]);

  // Auto-play listening audio: part instructions → group audio (1s gap).
  // Re-runs whenever position/instructions/interaction change.
  useEffect(() => {
    if (!hasUserInteracted || currentSection !== SECTION.LISTENING) return;
    audioRef.current?.pause();
    if (currentPartIndex < 0 || currentPartIndex >= partsLength) {
      setCurrentPartIndex(0);
      return;
    }

    if (showInstructions && currentPart?.audioUrl) {
      playAudio(currentPart.audioUrl);
    } else if (!showInstructions && currentGroup?.audioUrl) {
      (async () => {
        await delay(1000);
        playAudio(currentGroup.audioUrl);
      })();
    } else if (!showInstructions && !currentGroup && currentPart) {
      if (currentPartIndex < partsLength - 1) {
        setTimeout(() => handleNextPart(), 2000);
      }
    }
  }, [
    currentGroup,
    currentPart,
    showInstructions,
    hasUserInteracted,
    partsLength,
    currentPartIndex,
    currentSection,
    handleNextPart,
    playAudio,
  ]);

  // ── Reading timer (full mode only) ──────────────────────────────────
  useEffect(() => {
    if (mode !== "full") return;

    if (showInstructions) {
      // Auto-advance past the Reading directions screen after a brief dwell.
      if (currentSection === SECTION.READING) {
        setIsWaiting(true);
        const dwell = currentPartIndex === 4 ? 10000 : 6000;
        const t = setTimeout(() => {
          if (questionGroup.length) {
            setShowInstructions(false);
            setCurrentGroupIndex(0);
            setSeenPartInstructions((prev) =>
              prev.includes(currentPartIndex)
                ? prev
                : [...prev, currentPartIndex]
            );
          }
          setIsWaiting(false);
        }, dwell);
        return () => clearTimeout(t);
      }
      return undefined;
    }

    if (currentSection === SECTION.LISTENING) return undefined;

    // Timestamp-based timer so tab-switches / throttling don't drift.
    if (!timerEndTimeRef.current) {
      timerEndTimeRef.current = Date.now() + timeLeft * 1000;
    }
    const timer = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((timerEndTimeRef.current - Date.now()) / 1000)
      );
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        onTimeUpRef.current?.();
      }
    }, 100);
    return () => clearInterval(timer);
    // timeLeft intentionally excluded — only used as initial-end seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    currentPartIndex,
    currentSection,
    showInstructions,
    questionGroup.length,
  ]);

  // ── Manual navigation ───────────────────────────────────────────────
  const handleNext = useCallback(() => {
    if (showInstructions) {
      window.scrollTo(0, 0);
      if (questionGroup.length) {
        setShowInstructions(false);
        setCurrentGroupIndex(0);
        setSeenPartInstructions((prev) =>
          prev.includes(currentPartIndex) ? prev : [...prev, currentPartIndex]
        );
      } else if (currentPartIndex < partsLength - 1) {
        setCurrentPartIndex(currentPartIndex + 1);
        setShowInstructions(true);
        setCurrentGroupIndex(0);
      }
    } else if (
      questionGroup.length &&
      currentGroupIndex < questionGroup.length - 1
    ) {
      window.scrollTo(0, 0);
      setCurrentGroupIndex(currentGroupIndex + 1);
    } else if (currentPartIndex < partsLength - 1) {
      const nextPartIndex = currentPartIndex + 1;
      setCurrentPartIndex(nextPartIndex);
      // In part mode we never re-show instructions when crossing parts;
      // jumping straight to the questions keeps the practice flow snappy.
      if (mode !== "full" || seenPartInstructions.includes(nextPartIndex)) {
        setShowInstructions(false);
        setCurrentGroupIndex(0);
      } else {
        setShowInstructions(true);
        setCurrentGroupIndex(0);
        setSeenPartInstructions((prev) => [...prev, nextPartIndex]);
      }
    }
  }, [
    showInstructions,
    questionGroup.length,
    currentGroupIndex,
    currentPartIndex,
    partsLength,
    seenPartInstructions,
    mode,
  ]);

  const handlePrev = useCallback(() => {
    if (showInstructions) {
      if (currentPartIndex > 0) {
        const prevPartIndex = currentPartIndex - 1;
        setCurrentPartIndex(prevPartIndex);
        const prevPart = parts[prevPartIndex];
        if (prevPart?.questionGroup?.length > 0) {
          setCurrentGroupIndex(prevPart.questionGroup.length - 1);
          setShowInstructions(false);
        }
      } else {
        setShowInstructions(false);
        if (questionGroup.length > 0) setCurrentGroupIndex(0);
      }
    } else if (currentGroupIndex > 0) {
      setCurrentGroupIndex(currentGroupIndex - 1);
    } else if (currentPartIndex > 0) {
      const prevPartIndex = currentPartIndex - 1;
      setCurrentPartIndex(prevPartIndex);
      const prevPart = parts[prevPartIndex];
      if (prevPart?.questionGroup?.length > 0) {
        setCurrentGroupIndex(prevPart.questionGroup.length - 1);
        setShowInstructions(false);
      }
    }
  }, [
    showInstructions,
    currentPartIndex,
    currentGroupIndex,
    questionGroup.length,
    parts,
  ]);

  // ── Audio end handling ──────────────────────────────────────────────
  const handleAudioEnded = useCallback(() => {
    // End of the last listening group of the last listening part → reveal
    // the manual nav so the student can move to Reading on their schedule.
    if (
      currentGroupIndex === questionGroup.length - 1 &&
      currentPart?.partNumber === lastListeningPartNumber &&
      !showInstructions
    ) {
      setIsFinishListening(true);
      return;
    }

    if (currentSection !== SECTION.LISTENING) return;

    if (showWelcomeScreen) {
      setShowWelcomeScreen(false);
      setShowInstructions(true);
    } else if (showInstructions) {
      const grps = currentPart?.questionGroup;
      if (grps && grps.length > 0) {
        setCurrentGroupIndex(0);
        setShowInstructions(false);
      } else {
        audioTransitionTimerRef.current = setTimeout(() => {
          if (currentPartIndex < partsLength - 1) handleNextPart();
        }, 3000);
      }
    } else {
      audioTransitionTimerRef.current = setTimeout(() => {
        if (!currentPart?.questionGroup) return;
        const hasMore =
          currentGroupIndex < currentPart.questionGroup.length - 1;
        if (hasMore) {
          setCurrentGroupIndex(currentGroupIndex + 1);
        } else if (currentPartIndex < partsLength - 1) {
          handleNextPart();
        }
      }, 5000);
    }
  }, [
    currentSection,
    showWelcomeScreen,
    showInstructions,
    currentPart,
    partsLength,
    currentPartIndex,
    currentGroupIndex,
    questionGroup.length,
    lastListeningPartNumber,
    handleNextPart,
  ]);

  const handleAudioPlay = useCallback(() => setIsPlaying(true), []);

  // ── Question palette navigation ─────────────────────────────────────
  // Flat, sorted list of every question across every part — drives the
  // question-palette dialog.
  const allQuestions = useMemo(() => {
    const list = [];
    parts.forEach((part) => {
      (part.questionGroup || []).forEach((g) => {
        (g.questions || []).forEach((q) => list.push(q));
      });
    });
    return list.sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
  }, [parts]);

  // For the legacy ExamSession behaviour (Reading-only palette).
  const readingQuestions = useMemo(
    () =>
      allQuestions.filter((q) => {
        // No reliable per-question section flag; rely on part lookup.
        return parts.some(
          (part) =>
            part.section === SECTION.READING &&
            (part.questionGroup || []).some((g) =>
              (g.questions || []).some((qq) => qq._id === q._id)
            )
        );
      }),
    [allQuestions, parts]
  );

  const navigateToQuestion = useCallback(
    (questionNumber, { scope = "all" } = {}) => {
      const pool =
        scope === "reading"
          ? parts.filter((p) => p.section === SECTION.READING)
          : parts;
      for (const part of pool) {
        const groups = part.questionGroup || [];
        for (let gi = 0; gi < groups.length; gi++) {
          if (
            (groups[gi].questions || []).find(
              (q) => q.number === questionNumber
            )
          ) {
            const actualPartIndex = parts.findIndex(
              (p) => p.partNumber === part.partNumber
            );
            setCurrentPartIndex(actualPartIndex);
            setCurrentGroupIndex(gi);
            setShowInstructions(false);
            return true;
          }
        }
      }
      return false;
    },
    [parts]
  );

  // ── Reset (e.g. retake) ─────────────────────────────────────────────
  const resetFlow = useCallback(() => {
    setShowWelcomeScreen(mode === "full");
    setShowInstructions(mode === "full");
    setHasUserInteracted(mode !== "full");
    setCurrentPartIndex(0);
    setCurrentGroupIndex(0);
    setSeenPartInstructions([]);
    setIsFinishListening(false);
    setIsPlaying(false);
    setTimeLeft(READING_TIME_SECONDS);
    timerEndTimeRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    if (audioTransitionTimerRef.current) {
      clearTimeout(audioTransitionTimerRef.current);
      audioTransitionTimerRef.current = null;
    }
    window.scrollTo(0, 0);
  }, [mode]);

  // ── Resume directly at Reading (skip welcome + listening) ───────────
  // Called when the server confirms this is a resumed attempt already in
  // the Reading section. Jumps straight to `partIndex`/`groupIndex` with
  // the exact remaining timer seconds derived from when reading started.
  const resumeFrom = useCallback(
    ({
      partIndex = 0,
      groupIndex = 0,
      remainingSeconds = READING_TIME_SECONDS,
      startReadingTimer = true,
    }) => {
      if (audioTransitionTimerRef.current) {
        clearTimeout(audioTransitionTimerRef.current);
        audioTransitionTimerRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      setShowWelcomeScreen(false);
      setShowInstructions(false);
      setHasUserInteracted(true);
      setCurrentPartIndex(partIndex);
      setCurrentGroupIndex(groupIndex);
      setIsFinishListening(false);
      setIsPlaying(false);
      setSeenPartInstructions([]);
      const clamped = Math.max(0, Math.min(READING_TIME_SECONDS, remainingSeconds));
      setTimeLeft(clamped);
      // Only seed while actually resuming into Reading; if we are still in
      // Listening, defer timer start to the first Reading render.
      timerEndTimeRef.current = startReadingTimer
        ? Date.now() + clamped * 1000
        : null;
      window.scrollTo(0, 0);
    },
    []
  );

  // ── Pause helper (used by submit / unmount) ─────────────────────────
  const pauseAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    if (audioTransitionTimerRef.current) {
      clearTimeout(audioTransitionTimerRef.current);
      audioTransitionTimerRef.current = null;
    }
  }, []);

  return {
    // refs
    audioRef,
    // phase
    showWelcomeScreen,
    setShowWelcomeScreen,
    showInstructions,
    setShowInstructions,
    hasUserInteracted,
    setHasUserInteracted,
    // position
    currentPartIndex,
    currentGroupIndex,
    currentPart,
    currentGroup,
    questionGroup,
    currentSection,
    isLastQuestion,
    // audio
    audioVolume,
    setAudioVolume,
    isPlaying,
    isFinishListening,
    handleAudioEnded,
    handleAudioPlay,
    pauseAudio,
    // timer
    timeLeft,
    isWaiting,
    // navigation
    handleNext,
    handlePrev,
    handleNextPart,
    navigateToQuestion,
    // palettes
    allQuestions,
    readingQuestions,
    // misc
    resetFlow,
    resumeFrom,
  };
};

export default useExamFlow;
export { READING_TIME_SECONDS };
